import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { requestDeliveryQuote, toQuoteItems, quoteItemsKey } from "../api/deliveryQuote";
import { deliveryOfferOf } from "../utils/deliveryOffers";

export const QUOTE_DEBOUNCE_MS = 400;

/* Why a quote could not be shown, from the server's own error codes. */
export const estimateUnavailableReason = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (status === 401 || code === "NO_TOKEN" || code === "INVALID_TOKEN") return "needs_sign_in";
  if (code === "OUT_OF_DELIVERY_RANGE") return "out_of_range";
  return "error";
};

const isCoordinate = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

/* The drop-off the shopper has set (state.userLocation), if any. */
export const useDropoff = () => {
  const latitude = useSelector((state) => state.userLocation?.latitude);
  const longitude = useSelector((state) => state.userLocation?.longitude);
  const hasLocation = isCoordinate(latitude) && isCoordinate(longitude);
  return {
    hasLocation,
    latitude: hasLocation ? Number(latitude) : null,
    longitude: hasLocation ? Number(longitude) : null,
    locationKey: hasLocation ? `${Number(latitude)},${Number(longitude)}` : "",
  };
};

/**
 * What one vendor's quote says, in the estimate's words. Everything is READ
 * from the server's answer (POST /api/quote or one entry of POST
 * /api/quote/cart); nothing is added up here.
 *
 *   status        'disabled' | 'needs_location' | 'loading' | 'ready' |
 *                 'needs_sign_in' | 'out_of_range' | 'error'
 *   fee           the delivery fee, net of any offer, when ready
 *   freight       the fee is quoted separately (arranged freight)
 *   minutes       the server's delivery estimate
 *   offer         { kind, originalFee, waivedAmount } | null
 *   total         goods plus delivery, the server's sum, when ready
 *   goodsPriced   the server priced these exact lines (with or without an area)
 *   itemsSubtotal the server's goods subtotal
 *   minimumOrder  the server's { minimum, subtotal, shortfall, met } | null
 *   vendorName    the vendor (or branch) the server priced this cart from
 *
 * `outcome` is the last answer: { status, quote, itemsKey, locationKey }. An
 * answer for other lines or another drop-off is not this cart's answer, so
 * its fee reads as loading and its goods as not priced until the new one.
 */
export const estimateFrom = ({ enabled = true, hasLocation, itemsKey, locationKey, outcome }) => {
  const sameItems = Boolean(outcome) && outcome.itemsKey === itemsKey;
  const current = sameItems && outcome.locationKey === locationKey;
  let status;
  if (!enabled) status = "disabled";
  else if (!hasLocation) status = "needs_location";
  else if (!current || outcome.status === "loading") status = "loading";
  else status = outcome.status;

  const quote = status === "ready" ? outcome.quote || null : null;
  const freight = quote?.feeUnavailableReason === "freight_quoted_separately";
  const goods = enabled && sameItems ? outcome.quote : null;
  const goodsPriced = Boolean(goods) && goods.itemsSubtotal !== null && goods.itemsSubtotal !== undefined;
  return {
    status,
    hasLocation,
    fee: quote && !freight ? Number(quote.fee) || 0 : null,
    freight,
    minutes: quote ? Number(quote.estimatedDeliveryMinutes) || 0 : 0,
    offer: quote && !freight ? deliveryOfferOf(quote) : null,
    total: quote && quote.total !== null && quote.total !== undefined ? Number(quote.total) : null,
    goodsPriced,
    itemsSubtotal: goodsPriced ? Number(goods.itemsSubtotal) || 0 : null,
    minimumOrder: goodsPriced ? goods.minimumOrder || null : null,
    vendorName: (enabled && outcome?.quote?.vendorName) || null,
  };
};

/**
 * useDeliveryEstimate: one vendor's cart as the server prices it, before
 * checkout and before sign-in, so the total is never a surprise: the delivery
 * fee, the time, any free delivery, the goods subtotal, the total and the
 * minimum order verdict (estimateFrom above).
 *
 * Asks the same server quote the checkout uses (api/deliveryQuote) for the
 * drop-off the shopper has set (state.userLocation). With no area set yet it
 * still asks, for the goods only, so the minimum-order bar is the server's
 * answer from the first item. The client never computes a fee or a total.
 */
export default function useDeliveryEstimate({ vendorId, outletId, items, enabled = true } = {}) {
  const { hasLocation, latitude, longitude, locationKey } = useDropoff();
  const itemsKey = quoteItemsKey(items);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quoteItems = useMemo(() => toQuoteItems(items), [itemsKey]);
  const active = Boolean(enabled && vendorId);
  const wanted = active && (hasLocation || quoteItems.length > 0);

  const [outcome, setOutcome] = useState(null);
  const latestRequest = useRef(0);

  const fetchEstimate = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const keys = { itemsKey, locationKey };
    // The last answer stays under ITS keys while this one is asked, so it is
    // never read as the answer for lines it did not price.
    setOutcome((previous) => (previous ? { ...previous, status: "loading" } : { status: "loading", quote: null, ...keys }));
    try {
      const quote = await requestDeliveryQuote({
        vendorId,
        outletId,
        customerLat: latitude,
        customerLng: longitude,
        items: quoteItems,
        goodsOnly: !hasLocation,
      });
      if (requestId !== latestRequest.current) return;
      setOutcome({ status: "ready", quote, ...keys });
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setOutcome({ status: estimateUnavailableReason(error), quote: null, ...keys });
    }
  }, [vendorId, outletId, latitude, longitude, hasLocation, quoteItems, itemsKey, locationKey]);

  useEffect(() => {
    if (!wanted) return undefined;
    const timer = setTimeout(fetchEstimate, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [wanted, fetchEstimate]);

  return {
    ...estimateFrom({ enabled: active, hasLocation, itemsKey, locationKey, outcome }),
    refresh: fetchEstimate,
  };
}
